import {
	ActionButton,
	Button,
	NumberField,
	Text,
	TextArea,
} from '@react-spectrum/s2';
import Edit from '@react-spectrum/s2/icons/Edit';
import { useCallback, useEffect, useRef, useState } from 'react';

// The inline editor for one field of the guided strategy walk. It copies the shape of
// `strategy-dock/NorthStarRow.tsx`, and the split of duties is the important half: this component
// owns the DRAFT, and the caller owns the request. Nothing is lifted until the owner presses Save.
//
// Three rules come from that file and are not negotiable here.
//  - Explicit Save and Cancel. Never blur-autosave: a blur that saves loses a half-typed value the
//    owner was still deciding about.
//  - Escape is trapped. An untrapped Escape reaches the panel and closes the whole thing, so the
//    owner loses the edit and the part they were reading.
//  - Re-opening re-seeds the draft from the saved value, so a discarded draft can never come back.

type Common = {
	/** The eyebrow above the value, and the editor's own field label. */
	label: string;
	/** Names the control for a screen reader and for a test. */
	testId: string;
	/** What the collapsed row shows. The caller formats it, because only it knows the unit. */
	children: React.ReactNode;
	/** Copy for the control that opens the editor, for example "Change the number". */
	openLabel: string;
	disabled?: boolean;
};

type TextEdit = Common & {
	kind: 'text';
	value: string;
	onSave: (next: string) => Promise<void>;
};

type NumberEdit = Common & {
	kind: 'number';
	value: number | null;
	unit?: string;
	/** The lowest figure this field accepts. Defaults to 1, which is right for a TARGET: rule R26
	 *  makes it a total you reach, so zero is not a goal. A BASELINE is where the owner stands
	 *  today, and zero is the commonest honest answer, so that field passes 0. */
	minValue?: number;
	onSave: (next: number) => Promise<void>;
};

export const StepFieldEdit = (props: TextEdit | NumberEdit) => {
	const { label, testId, children, openLabel, disabled } = props;
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [draftText, setDraftText] = useState('');
	const [draftNumber, setDraftNumber] = useState<number | null>(null);
	const editorRef = useRef<HTMLDivElement | null>(null);

	const open = useCallback(() => {
		// Seed from the SAVED value every time, so a draft the owner cancelled never returns.
		if (props.kind === 'text') setDraftText(props.value ?? '');
		else setDraftNumber(props.value ?? null);
		setError(null);
		setEditing(true);
	}, [props]);

	const close = useCallback(() => {
		setEditing(false);
		setError(null);
	}, []);

	useEffect(() => {
		if (editing) editorRef.current?.focus();
	}, [editing]);

	const minValue = props.kind === 'number' ? (props.minValue ?? 1) : 1;
	const canSave =
		props.kind === 'text'
			? draftText.trim().length > 0 && draftText.trim() !== props.value?.trim()
			: draftNumber != null &&
				Number.isFinite(draftNumber) &&
				draftNumber >= minValue &&
				draftNumber !== props.value;

	const save = useCallback(async () => {
		if (!canSave || saving) return;
		setSaving(true);
		setError(null);
		try {
			if (props.kind === 'text') await props.onSave(draftText.trim());
			else await props.onSave(draftNumber as number);
			setEditing(false);
		} catch (err) {
			// The hook already turned the status into owner-facing copy. Show it in place, so the
			// owner keeps their draft and can try again without retyping.
			setError(
				err instanceof Error
					? err.message
					: "We couldn't save that change. Please try again.",
			);
		} finally {
			setSaving(false);
		}
	}, [canSave, saving, props, draftText, draftNumber]);

	if (!editing) {
		return (
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0 flex-1 space-y-1">
					<p
						className="text-xs font-semibold tracking-wide text-(--text-muted)
							uppercase"
					>
						{label}
					</p>
					{children}
				</div>
				{!disabled && (
					// A quiet glyph, not the word "Change". A campaign card carries four of these
					// rows, and four repetitions of the same word read as four things to do rather
					// than one affordance. Right-aligned, the glyphs read as a single edit rail.
					// `openLabel` still names the field for a screen reader and for a test.
					<span data-testid={`${testId}-open`} className="shrink-0">
						<ActionButton isQuiet aria-label={openLabel} onPress={open}>
							<Edit />
						</ActionButton>
					</span>
				)}
			</div>
		);
	}

	return (
		<div
			ref={editorRef}
			tabIndex={-1}
			data-testid={`${testId}-editor`}
			className="space-y-3 rounded-lg border border-(--glass-border) px-3 py-3"
			onKeyDown={(e) => {
				// Trapped on purpose. Un-trapped, this reaches the panel and closes it.
				if (e.key === 'Escape') {
					e.stopPropagation();
					// A save in flight owns this editor. Cancel is already disabled while it
					// runs, and Escape must not become the one exit that ignores that. The
					// error a rejected save writes lives in the editing branch below, so an
					// early close makes it unreachable and the owner is told nothing.
					if (saving) return;
					close();
				}
			}}
		>
			{props.kind === 'text' ? (
				<TextArea
					label={label}
					value={draftText}
					onChange={setDraftText}
					isDisabled={saving}
				/>
			) : (
				<NumberField
					label={label}
					value={draftNumber ?? undefined}
					onChange={(n) => setDraftNumber(Number.isNaN(n) ? null : n)}
					minValue={minValue}
					isDisabled={saving}
				/>
			)}
			{error && (
				<p data-testid={`${testId}-error`} className="text-sm text-red-700">
					{error}
				</p>
			)}
			<div className="flex items-center justify-end gap-2">
				<Button variant="secondary" onPress={close} isDisabled={saving}>
					<Text>Cancel</Text>
				</Button>
				<span data-testid={`${testId}-save`}>
					<Button
						variant="accent"
						onPress={() => void save()}
						isDisabled={!canSave || saving}
						isPending={saving}
					>
						<Text>Save</Text>
					</Button>
				</span>
			</div>
		</div>
	);
};

/**
 * A clamped paragraph that reveals a `More` control ONLY when it really overflows.
 *
 * The measurement is the point. A clamp with an always-on toggle puts a dead affordance on short
 * text; a clamp with NO toggle hides content silently, which is worse. `design.md` documents this
 * recipe for the strategy card's campaign rows, and the same rules apply here:
 *  - the clamp goes on a LEAF `<p>`, never a container. On a container `line-clamp` counts TOTAL
 *    lines, so later siblings render completely invisible.
 *  - no other `display` utility on that element, or the clamp silently no-ops.
 *  - `min-h-6` on the toggle, for the 24px target.
 *  - no height transition: `line-clamp` is not animatable.
 */
const CLAMP = {
	2: 'line-clamp-2',
	3: 'line-clamp-3',
	4: 'line-clamp-4',
} as const;

export const ClampedProse = ({
	text,
	children,
	lines = 3,
	className,
	testId,
}: {
	/** The plain text. Still required when `children` is given: it is what the measurement keys
	 *  off, so a caller that only passed rich children would never re-measure on a content change. */
	text: string;
	/** Rich content for the SAME clamped `<p>`, for a caller that needs to wrap the words — a
	 *  glossary tooltip, for instance. It must render the same words as `text`. */
	children?: React.ReactNode;
	lines?: 2 | 3 | 4;
	className?: string;
	testId?: string;
}) => {
	const ref = useRef<HTMLParagraphElement | null>(null);
	const [expanded, setExpanded] = useState(false);
	const [overflows, setOverflows] = useState(false);
	const bodyId = `${testId ?? 'prose'}-body`;

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		// Measure against the CLAMPED box, so this reports whether a toggle is warranted at all.
		const measure = () => {
			if (!expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
		};
		measure();
		// WIDTH changes the answer, and it is in no dependency here: the panel sits in a resizable
		// split, so narrowing it makes text overflow that did not before. Without this the toggle
		// kept its first-paint value and stayed hidden, while `line-clamp` — which is CSS and needs
		// no effect — went on hiding the extra lines. The text was cut and the control that would
		// reveal it was absent (ADO-1984). `StrategyCard.tsx:170-186` already does exactly this.
		const ro =
			typeof ResizeObserver !== 'undefined'
				? new ResizeObserver(measure)
				: null;
		ro?.observe(el);
		return () => ro?.disconnect();
	}, [text, expanded]);

	return (
		<>
			<p
				ref={ref}
				id={bodyId}
				data-testid={testId}
				className={
					// Written out, never interpolated: Tailwind scans source text, so a class it
					// cannot see as a literal is never generated.
					expanded ? className : `${CLAMP[lines]} ${className ?? ''}`
				}
			>
				{children ?? text}
			</p>
			{(overflows || expanded) && (
				<button
					type="button"
					aria-expanded={expanded}
					aria-controls={bodyId}
					data-testid={testId ? `${testId}-toggle` : undefined}
					onClick={() => setExpanded((v) => !v)}
					className="min-h-6 text-xs font-semibold text-(--text-muted)
						hover:text-(--text-primary)"
				>
					{expanded ? 'Less' : 'More'}
				</button>
			)}
		</>
	);
};
